import { FaChevronRight, FaStar } from "react-icons/fa6";
import useTiltSpotlight from "../../hooks/useTitlSpotLight";
import Image from "next/image";

const toneStyles = {
  aurora: {
    ring: "ring-cyan-400/40",
    glow: "from-cyan-500/25 via-blue-500/10 to-fuchsia-500/25",
    chip: "bg-cyan-400/15 text-cyan-100 ring-cyan-300/30",
    iconBg: "bg-gradient-to-br from-cyan-500 to-blue-600",
  },
  goldRush: {
    ring: "ring-yellow-400/40",
    glow: "from-yellow-500/25 via-orange-500/10 to-rose-500/25",
    chip: "bg-yellow-300/15 text-yellow-50 ring-yellow-200/30",
    iconBg: "bg-gradient-to-br from-yellow-500 to-orange-600",
  },
  royalViolet: {
    ring: "ring-violet-400/40",
    glow: "from-violet-500/30 via-purple-500/10 to-fuchsia-500/30",
    chip: "bg-violet-400/15 text-violet-50 ring-violet-200/30",
    iconBg: "bg-gradient-to-br from-violet-500 to-fuchsia-600",
  },
};

const AwardCard = ({ award, onOpen }) => {
  const Icon = award.icon ?? FaStar;
  const tone = toneStyles[award.tone] ?? toneStyles.aurora;
  const tilt = useTiltSpotlight();

  return (
    <article
      ref={tilt.ref}
      onClick={() => onOpen(award)}
      onMouseMove={tilt.onMove}
      onMouseLeave={tilt.onLeave}
      style={tilt.tiltStyle}
      className={[
        "group relative overflow-hidden rounded-[2rem]",
        "border border-white/10 bg-white/[0.04]",
        "shadow-[0_0_0_1px_rgba(255,255,255,0.02),0_14px_50px_rgba(0,0,0,0.7)]",
        "backdrop-blur-xl transition-all duration-300",
        "hover:-translate-y-1 hover:border-white/20 hover:bg-white/[0.06]",
        "will-change-transform cursor-pointer",
      ].join(" ")}
    >
      {/* spotlight cursor */}
      <div
        className={`pointer-events-none absolute inset-0 transition-opacity duration-300 ${
          tilt.spot.show ? "opacity-100" : "opacity-0"
        }`}
        style={{
          background: `radial-gradient(500px circle at ${tilt.spot.x}% ${tilt.spot.y}%, rgba(255,255,255,0.10), transparent 40%)`,
        }}
      />

      {/* glowing wash */}
      <div
        className={[
          "pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100",
          "bg-gradient-to-br",
          tone.glow,
        ].join(" ")}
      />

      {/* image */}
      <div className="relative h-48 w-full overflow-hidden">
        <Image
          src={award.image}
          alt={award.title}
          width={500}
          height={500}
          className="h-full w-full object-cover opacity-95 transition duration-700 group-hover:scale-[1.08]"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />

        <div className="absolute right-3 top-3 rounded-full bg-black/60 px-3 py-1 text-xs font-semibold text-white ring-1 ring-white/10">
          {award.year}
        </div>
      </div>

      {/* content */}
      <div className="relative p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div
              className={[
                "grid h-11 w-11 place-items-center rounded-2xl text-white shadow-lg ring-1",
                tone.iconBg,
                tone.ring,
              ].join(" ")}
            >
              <Icon className="text-lg drop-shadow" />
            </div>

            <div>
              <div className="text-xs text-white/60">{award.org}</div>
              <div className="mt-0.5 text-sm font-medium text-white/90">
                {award.subtitle}
              </div>
            </div>
          </div>

          <span
            className={[
              "shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1",
              tone.chip,
            ].join(" ")}
          >
            {award.badge}
          </span>
        </div>

        <h3 className="mt-4 text-lg font-semibold leading-snug tracking-tight">
          {award.title}
        </h3>

        <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-white/70">
          {award.description}
        </p>

        <div className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-white/80 transition group-hover:text-white">
          View highlight
          <FaChevronRight className="text-xs transition group-hover:translate-x-0.5" />
        </div>
      </div>

      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent opacity-60" />
    </article>
  );
};

export default AwardCard;
